/**
 * Main application layout using @signanthealth/modular-ui AppLayout components.
 * Follows the pattern from study-management-client.
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { AppLayout as MuiAppLayout } from '@signanthealth/modular-ui';
import { config } from '../config';
import { useApiAuth } from '../hooks/useApiAuth';
import { useTokenRefreshOnActivity } from '../hooks/useTokenRefreshOnActivity';

interface AppLayoutProps {
  toggleTheme?: () => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({ toggleTheme }) => {
  const theme = useTheme();
  const { authState, oktaAuth } = useOktaAuth();
  const [userInitials, setUserInitials] = useState<string>('');

  // Synchronize Okta access token with API services
  useApiAuth();
  // Proactive token refresh on user activity (throttled to avoid flooding Okta)
  useTokenRefreshOnActivity();

  // Get user info from Okta
  useEffect(() => {
    if (authState?.isAuthenticated && authState.idToken?.claims) {
      const claims = authState.idToken.claims;
      const firstName = (claims.firstName as string) ?? (claims.given_name as string) ?? '';
      const lastName = (claims.lastName as string) ?? (claims.family_name as string) ?? '';

      setUserInitials(`${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase() || 'U');
    }
  }, [authState]);

  const handleLogout = async () => {
    await oktaAuth.signOut();
  };

  // Theme-aware background color (matches study-management-client pattern)
  const backgroundColor =
    theme.palette.mode === 'light' ? '#F6F6F6' : theme.palette.background.default;

  return (
    <MuiAppLayout.Root>
      {/* Header - modular-ui standard */}
      <MuiAppLayout.Header
        appBarSxProps={{ position: 'relative' }}
        brandName={config.brandName}
        appName={config.appName}
        homeUrl={config.applicationPath || '/'}
        toggleTheme={toggleTheme}
        userInitials={userInitials}
        logout={handleLogout}
        trademarkSymbol="®"
      />

      {/* Content area - flex: 1 ensures it grows to push footer to bottom */}
      <MuiAppLayout.Content sx={{ flex: 1 }}>
        <MuiAppLayout.Main
          sx={{
            padding: '0',
            backgroundColor,
            minHeight: '100%',
          }}
        >
          <Outlet />
        </MuiAppLayout.Main>
      </MuiAppLayout.Content>

      {/* Footer - modular-ui standard */}
      <MuiAppLayout.Footer
        paperProps={{
          sx: {
            zIndex: 1,
            position: 'relative',
          },
        }}
        appName={config.appName}
        appVersion={config.appVersion}
      />
    </MuiAppLayout.Root>
  );
};

export default AppLayout;
